import swal, { SweetAlertCustomClass, SweetAlertIcon, SweetAlertResult } from "sweetalert2";
import { Entrada } from '../interfaces/device';
import { TranslateService } from '@ngx-translate/core';
import { NewDevice } from '../interfaces/connection-devices';
import { minimizeSidebar } from "./sidebar.utils";
import { MIN_NUMBER_BLOCKS } from '../constants/project';
import { PARAM_NAME_PATTERN } from "../constants/regex";
import { MAX_CHARACTERS } from "../constants/global";
import { App } from '@capacitor/app'
import { KeyboardVerification } from "../interfaces/commands";
import { ProjectClass } from "src/app/utils/project.util";

/**
 * Allow select Input+15 of project
 * @param {Array<Entrada>} inputs_list - Input list in project
 * @param {TranslateService} _translate - Translate service
 * @returns {Promise<SweetAlertResult<any>>}
 */
export const swalSelectInput = (inputs_list: Array<Entrada>, _translate: TranslateService): Promise<SweetAlertResult<any>> => {
  // Filter only digital inputs
  inputs_list = inputs_list.filter((m) => !['5', '6', '7'].includes(m.logica_activa));
  let Inputs: any = inputs_list.reduce((newObject: any, input: Entrada) => ({ ...newObject, [input.id]: input.nombre }), {});
  //console.log(Inputs);
  return swal.fire({
    title: _translate.instant('Transfer.MESSAGES.INPUT_15'),
    text: _translate.instant('Transfer.MESSAGES.SELECT_INPUT_15'),
    input: 'select',
    inputOptions: Inputs,
    inputPlaceholder: _translate.instant('Device_admin.LABELS.SELECT_OPTION'),
    allowEscapeKey: false,
    allowEnterKey: false,
    showCancelButton: true,
    cancelButtonText: _translate.instant('GeneralOriginal.CANCEL'),
    confirmButtonText: _translate.instant('GeneralOriginal.SAVE'),
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-success",
      cancelButton: "btn",
      input: 'swal2-select form-control',
    },
    buttonsStyling: false
  });
}

/**
 * Muestra en pantalla un mensaje de advertencia ante una acción
 * @param {string} title - Titulo del mensaje de advertencia
 * @param {string} text - Texto indicativo
 * @param {TranslateService} traslate - Servicio Traducción
 * @param {string} button - Nombre que tomará el botón
 * @returns {Promise<SweetAlertResult<any>>}
 */
/**
 * Pregunta al usuario si el proyecto tiene un dispositivo "Rutero", antes de generar la Tabla de Cargas.
 * @param {TranslateService} _translate - Servicio de Traducción
 * @returns {Promise<SweetAlertResult<any>>}
 */
export const swalHasRuteroQuestion = (_translate: TranslateService): Promise<SweetAlertResult<any>> => {
  minimizeSidebar();
  const Title: string = _translate.instant('Device_admin.MESSAGES.HAS_RUTERO_TITLE');
  const Text: string = _translate.instant('Device_admin.MESSAGES.HAS_RUTERO_MESSAGE');
  return swal.fire({
    title: Title,
    text: Text,
    showCancelButton: true,
    confirmButtonText: _translate.instant('GeneralOriginal.YES'),
    cancelButtonText: _translate.instant('GeneralOriginal.NO'),
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-success",
      cancelButton: "btn btn-danger"
    },
    buttonsStyling: false
  });
}

export const swalWarningMessage = (
  title: string,
  text: string,
  traslate: TranslateService,
  button?: string,
  style?: SweetAlertCustomClass
): Promise<SweetAlertResult<any>> => {
  //Se minimiza menú lateral de navgeación al abrir Swal
  minimizeSidebar();
  const Button: string = traslate.instant(`GeneralOriginal.${button ? button : 'DELETE'}`);
  return swal.fire({
    title,
    text,
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: traslate.instant('GeneralOriginal.CANCEL'),
    confirmButtonText: Button,
    reverseButtons: true,
    customClass: style ?? {
      confirmButton: "btn btn-success",
      cancelButton: "btn btn-danger"
    },
    buttonsStyling: false
  });
}

/**
 * Mensaje de carga
 * @param {string} title 
 * @param {string} text 
 * @returns {Promise<SweetAlertResult<any>>}
 */
export const swalLoading = (title: string, text: string): Promise<SweetAlertResult<any>> => {
  return swal.fire({
    title,
    text,
    showConfirmButton: false,
    width: '450px',
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    willOpen: () => {
      swal.showLoading()
    }
  });
}

/**
 * Cierra el Mensaje
 * @returns {void}
 */
export const swalClose = (): void => swal.close();

/**
 * Mensaje que confirma una acción 
 * @param {string} title 
 * @param {string} text 
 * @param {SweetAlertIcon} icon 
 * @returns {void}
 */
export const swalConfirmAction = (title: string, text: string, icon: SweetAlertIcon = 'success', button_label?: string): Promise<SweetAlertResult<any>> => {
  //Se minimiza menú lateral de navgeación al abrir Swal
  minimizeSidebar();
  return swal.fire({
    title,
    text,
    icon,
    customClass: {
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
    confirmButtonText: (button_label ?? 'Ok')
  });
}

/**
 * Mensaje que confirma la cancelación de un proceso
 * @param {string} title 
 * @param {string} text 
 * @param {SweetAlertIcon} icon 
 * @returns {void}
 */
export const swalCancel = (title: string, text: string, icon: SweetAlertIcon = 'error'): void => {
  //Se minimiza menú lateral de navgeación al abrir Swal
  minimizeSidebar();
  swal.fire({
    title,
    text,
    icon,
    customClass: {
      confirmButton: "btn btn-danger",
    },
    buttonsStyling: false
  });
}

/**
 * Mensaje de búsqueda de dispositivos Mowin
 * @param {any} args 
 * @returns {Promise<SweetAlertResult<any>>}
 */
export const swalSearchMowinDevices = ({ title, progressHTML }: any, traslate: TranslateService): Promise<SweetAlertResult<any>> => {
  return swal.fire({
    html: `
      <div class="row">
        <div class="col-12">
          <h5 class="text-center title mt-0">${title}</h5>
        </div>
        <div class="box-select-devices col-12">
          ${progressHTML}
          <div class="col-12">
            <div class="spinner-border text-info" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    `,
    showCancelButton: true,
    cancelButtonText: traslate.instant('GeneralOriginal.CANCEL'),
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    didOpen: () => ({ isOpen: true }),
    preConfirm: () => {
      const Input: any = document.querySelector('#selectComDevices');
      return Input.value;
    }
  });
}

/**
 * Permite editar el contenido del mensaje de búsqueda de dispositivos Mowin, precisamente el contador de búsqueda
 * @param {any} args 
 * @returns {void}
 */
export const editSwalSearchContent = ({ selector, newContent }: any): void => {
  swal.getContent().querySelector(selector).textContent = newContent;
}

/**
 * Permite editar todo el contenido del mensaje de búsqueda de dispositivos Mowin.
 * @param {any} args 
 * @returns {void}
 */
export const editSwalSearchHTMLContent = ({ selector, newContent }): void => {
  swal.getContent().querySelector(selector).innerHTML = newContent;
  swal.getActions().style.display = 'flex';
  swal.getActions().style.marginTop = '0';
  swal.getConfirmButton().style.display = 'flex';
}

/**
 * Genera un string con los diferentes option que tendra un Select.
 * @param {Array<NewDevice>} devices - Lista de dispositivos Mowin disponibles
 * @param {TranslateService} _translate - Servicio de traducción
 * @returns {string}
 */
export const generateSelectHTML = (devices: Array<NewDevice>, _translate: TranslateService): string => {
  const SelectHTML = devices.map(device => `<option value="${device.id}">${device.name}</option>`);
  SelectHTML.unshift(`<option selected disabled value="">${_translate.instant('Device_admin.LABELS.SELECT_OPTION')}</option>`);
  return SelectHTML.join('');
}

/**
 * Mensaje con Select
 * @param {string} title - Titulo del Sweet Alert
 * @param {string} content - Contenido
 * @param {TranslateService} _translate - Servicio Traducción
 * @returns {Promise<SweetAlertResult<any>>}
 */
export const swalSelectByHTML = (title: string, content: string, _translate: TranslateService): Promise<SweetAlertResult<any>> => {
  return swal.fire({
    title,
    html: `
      <select id="swalSelect1" class="form-control">
        ${content}
      </select>`,
    confirmButtonText: _translate.instant('Connection.LABELS.CONNECT'),
    showConfirmButton: true,
    showCancelButton: false,
    allowEnterKey: false,
    preConfirm: () => {
      const Input: any = document.querySelector('#swalSelect1');
      return Input.value;
    }
  });
}

/**
 * Permite el ingreso del nombre de los bloques tipo Señal Salida
 * @param {TranslateService} _translate
 * @returns {Promise<string>}
 */
export const swalSignName = async (_translate: TranslateService): Promise<string> => {
  // Se minimiza menú lateral de navgeación al abrir Swal
  minimizeSidebar();
  const BlockType: string = _translate.instant('Operation_logic.BLOCKS.SIGNS.SIGN_OUTPUT');
  const Title: string = _translate.instant('Operation_logic.LABELS.BLOCK', { BlockType });
  const { value } = await swal.fire({
    title: Title,
    input: 'text',
    inputPlaceholder: _translate.instant('Operation_logic.LABELS.PLACEHOLDER_SIGN_OUT'),
    allowEnterKey: true,
    allowEscapeKey: true,
    allowOutsideClick: false,
    showCancelButton: true,
    confirmButtonText: _translate.instant('GeneralOriginal.SAVE'),
    cancelButtonText: _translate.instant('GeneralOriginal.CANCEL'),
    customClass: {
      'cancelButton': "btn btn-default",
      'confirmButton': "btn btn-success",
      'input': 'form-control',
    },
    buttonsStyling: false,
    reverseButtons: true,
    inputValidator: (value) => {
      if (!value) {
        return _translate.instant('Operation_logic.ERRORS.INPUT_EMPTY')
      } else if (value.length > 15) {
        return _translate.instant('Operation_logic.ERRORS.NAME_TOO_LONG')
      }
    }
  //   preConfirm: (value) => {
  //      return ProjectClass.removeSpecialCharacters(value);
  //    },
  //    didOpen: () => {
  //      const inputEl = document.querySelector('.swal2-input') as HTMLInputElement;
  //      if (inputEl) {
  //        inputEl.addEventListener('input', () => {
  //          const cursorPos = inputEl.selectionStart ?? inputEl.value.length;
  //          const normalized = ProjectClass.removeSpecialCharacters(inputEl.value);
  //          inputEl.value = normalized;
  //          inputEl.setSelectionRange(cursorPos, cursorPos);
  //        });
  //      }
  //    }
  });
  return value;
}

/**
 * Swal Informativo
 * @param {string} title 
 * @param {string} text 
 * @param {SweetAlertIcon} icon 
 */
export const SwalInfo = (title: string, text: string, icon: SweetAlertIcon = 'warning'): void => {
  swal.fire({
    title,
    text,
    icon
  });
}

/**
 * Mensaje de compilación de estructuras.
 * @param {TranslateService} _translate - Servicio Translate 
 * @param {boolean} custom_blocks - ¿compilación orientada a Bloques Personalizados?
 * @returns {Promise<SweetAlertResult<any>>}
 */
export const SwalCompilationStructures = (_translate: TranslateService, custom_blocks: boolean = false): Promise<SweetAlertResult<any>> => {
  const Code: string = (custom_blocks ? 'Custom_blocks.TITLES.COMPILING_CUSTOM_BLOCKS' : 'Transfer.TITLES.COMPILING_STRUCTURES');
  const Title: string = _translate.instant(Code);
  return swal.fire({
    html: `<div class="row"><div class="col-12"><h5 class="text-center title mt-0">${Title}</h5></div>` +
      `<div class="box-select-devices col-12"><p class="col-12 font-weight-normal"></p><div class="col-12">` +
      `<div class="spinner-border text-info" role="status"><span class="sr-only">Loading...</span></div></div></div></div>`,
    showCancelButton: true,
    cancelButtonText: _translate.instant('GeneralOriginal.CANCEL'),
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    didOpen: () => ({ isOpen: true })
  });
}

/**
 * Permite editar el contenido del mensaje de compilanción de estructuras.
 * @param {string} selector - Etiqueta a editar
 * @param {TranslateService} _translate - Servicio Translate
 * @param {any} options - { counter: No. de estructura actual, structures_qty: Total de estructuras }
 * @param {boolean} custom_blocks - ¿compilación orientada a Bloques Personalizados?
 * @returns {void}
 */
export const editSwalCompilationStructures = (selector: string, _translate: TranslateService, options: any, custom_blocks: boolean = false): void => {
  const Code: string = (custom_blocks ? 'Custom_blocks.LABELS.COMPILING_CUSTOM_BLOCKS' : 'Transfer.LABELS.COMPILING_STRUCTURES');
  const NewContent: string = _translate.instant(Code, options)
  swal.getContent().querySelector(selector).textContent = NewContent;
}

/**
 * Mensaje de reporte de Estructuras.
 * @param {TranslateService} _translate - Servicio Translate
 * @param {Array<any>} results - Lista de Resultados
 * @param {boolean} valid_logic - ¿Mostrar mensaje? (Opcional)
 * @param {boolean} custom_blocks - ¿compilación orientada a Bloques Personalizados?
 * @returns
 */
export const swalStructuresReport = (_translate: TranslateService, results: Array<any>, valid_logic?: boolean, custom_blocks: boolean = false): Promise<SweetAlertResult<any>> => {
  minimizeSidebar();
  const TitleCode: string = (custom_blocks ? 'Custom_blocks.TITLES.CUSTOM_BLOCKS_REPORT' : 'Transfer.TITLES.STRUCTURES_REPORT');
  const TextCode: string = (custom_blocks ? 'Custom_blocks.LABELS.CUSTOM_BLOCKS_ERRRORS' : 'Transfer.LABELS.STRUCTURES_ERRRORS');
  const Title: string = _translate.instant(TitleCode);
  const Text: string = _translate.instant(TextCode);
  const ResultList: string = results.map(({ structure, status, message, type }: any) => {
    const CheckIcon: string = 'fa fa-check-circle';
    const ErrorIcon: string = 'fa fa-times-circle';
    const InfoIcon: string = 'fa fa-info-circle';
    return `<tr><td>${structure}</td><td class="text-${type}">` +
      `<i class="${(type === 'info') ? InfoIcon : (status ? CheckIcon : ErrorIcon)}">` +
      `</i> ${_translate.instant(message)}</td></tr>`;
  }).join('');
  const LogicSizeMessage: string = `<blockquote class="blockquote mb-0 mt-1 ml-0 mr-0 p-2 text-left font-weight-normal">` +
    `<div class="row"><div class="col-1"><i class="now-ui-icons travel_info text-info"></i></div>` +
    `<div class="col-11"><p class="msg-min-blocks mb-0">` +
    `${_translate.instant('Operation_logic.WARNINGS.MINIMUN_BLOCKS', { minimum: MIN_NUMBER_BLOCKS })}` +
    `</p></div></div></blockquote>`;
  return swal.fire({
    width: '800px',
    html: `<div class="row"><div class="col-12"><h5 class="text-center title mt-0">${Title}</h5>` +
      `<p class="font-weight-normal">${Text}</p></div><div class="col-12 mt-2" style="height: 300px; overflow-y: scroll;">` +
      `<table class="table"><tbody>${ResultList}</tbody></table></div></div>${!valid_logic ? LogicSizeMessage : ''}`,
    showCancelButton: true,
    cancelButtonText: _translate.instant('GeneralOriginal.CLOSE'),
    showConfirmButton: false,
    allowOutsideClick: true,
    allowEscapeKey: true,
    allowEnterKey: false,
    didOpen: () => ({ isOpen: true })
  });
}


/**
 * Permite el ingreso del nombre de los bloques personalizados
 * @param _translate - Servicio '_translate'
 * @param {string} old_name - Nombre anterior (Opcional)
 * @returns {Promise<{ status: boolean, name: string }>}
 */
export const swalCustomBlockName = (_translate: TranslateService, old_name?: any): Promise<{ status: boolean, name: string }> => {
  return new Promise<{ status: boolean, name: string }>(async (resolve) => {
    const { value, isDismissed } = await swal.fire({
      title: _translate.instant('Custom_blocks.TITLES.CUSTOM_BLOCK'),
      input: 'text',
      inputValue: (old_name ?? ''),
      inputPlaceholder: _translate.instant('Custom_blocks.LABELS.PLACEHOLDER_CUSTOM_BLOCK'),
      allowEnterKey: true,
      allowEscapeKey: true,
      allowOutsideClick: false,
      showCancelButton: true,
      cancelButtonText: _translate.instant('GeneralOriginal.CANCEL'),
      confirmButtonText: _translate.instant('GeneralOriginal.SAVE'),
      customClass: {
        'cancelButton': 'btn',
        'confirmButton': "btn btn-success",
        'input': 'form-control',
      },
      buttonsStyling: false,
      reverseButtons: true,
      inputValidator: (value) => {
        if (!value) {
          return _translate.instant('Custom_blocks.ERRORS.INPUT_EMPTY');
        } else if (value.length > 15) {
          return _translate.instant('Operation_logic.ERRORS.NAME_TOO_LONG');
        }
      }
    });
    resolve({ status: isDismissed, name: value });
  });
}

/**
 * Permite el cambio del nombre de las estructuras.
 * @param {TranslateService} _translate - Servicio de Traducción
 * @param {string} old_name - Nombre Anterior de la estructura
 * @returns {Promise<{ status: boolean, name: string}>}
 */
export const swalRenamStructure = (_translate: TranslateService, old_name: string): Promise<{ status: boolean, name: string}> => {
  return new Promise<{ status: boolean, name: string}>(async (resolve) => {
    const { value, isDismissed }: SweetAlertResult<any> = await swal.fire({
      title: _translate.instant('Operation_logic.LABELS.STRUCTURE_NAME'),
      input: 'text',
      inputValue: old_name,
      inputPlaceholder:  _translate.instant('Operation_logic.LABELS.ENTER_STRUCTURE_NAME'),
      allowEnterKey: true,
      allowEscapeKey: true,
      allowOutsideClick: false,
      showCancelButton: true,
      cancelButtonText: _translate.instant('GeneralOriginal.CANCEL'),
      confirmButtonText: _translate.instant('GeneralOriginal.SAVE'),
      customClass: {
        'cancelButton': 'btn',
        'confirmButton': "btn btn-success",
        'input': 'form-control',
      },
      buttonsStyling: false,
      reverseButtons: true,
      inputValidator: (value) => {
        const Code: string = 'Operation_logic.ERRORS.RENAME_STRUCTURE';
        const Pattern: boolean = PARAM_NAME_PATTERN.test(value);
        if(!value) return _translate.instant(`${Code}.EMPTY`);
        if(!Pattern) return _translate.instant(`${Code}.PATTERN`);
        if(value.length > MAX_CHARACTERS) return _translate.instant(`${Code}.MAX_LENGTH`, { maxlength: MAX_CHARACTERS});
      }
    //  preConfirm: (value) => {
    //    return ProjectClass.removeSpecialCharacters(value);
    //  },
    //  didOpen: () => {
    //    const inputEl = document.querySelector('.swal2-input') as HTMLInputElement;
    //    if (inputEl) {
    //      inputEl.addEventListener('input', () => {
    //        const cursorPos = inputEl.selectionStart ?? inputEl.value.length;
    //        const normalized = ProjectClass.removeSpecialCharacters(inputEl.value);
    //        inputEl.value = normalized;
    //        inputEl.setSelectionRange(cursorPos, cursorPos);
    //      });
    //    }
    //  }
    //  
    });
    resolve({ status: isDismissed, name: value });
  });
}

export const swalOldProjectAlert = async (_translate: TranslateService,projectDate: string): Promise<boolean> => {
  // Intentamos extraer el año de la fecha
  const year = projectDate ? parseInt(projectDate.split('-')[2]) : null;

  // Si no hay fecha o no es del 2022, no mostrar alerta
  if (!year || year !== 2022) {
    return false;
  }

  // Mostrar alerta personalizada
  const { isConfirmed } = await swal.fire({
    icon: 'warning',
    title: _translate.instant('Project_admin.WARNINGS.OLD_PROJECT_TITLE'),
    html: _translate.instant('Project_admin.WARNINGS.OLD_PROJECT'),
    showCancelButton: false,
    confirmButtonText: _translate.instant('GeneralOriginal.CONTINUE'),
    customClass: {
      confirmButton: 'btn btn-success',
    },
    buttonsStyling: false,
  });

  return isConfirmed;
};

/**
 * Allow open dialog for delete the local storage from port name
 * @param {TranslateService} _translate - Translation service
 * @param {Promise<void>} load_device - Load devices list
 * @param {Array<string>} list_hide - List of device to hide
 * @returns {Promise<void>}
 */
export const swalDeleteFromLocalStorage = (_translate: TranslateService, load_device: Promise<void>, list_hide: Array<string>): Promise<void> => {
  return swal.fire({
    title: _translate.instant('GeneralOriginal.LABELS.ARE_YOU_SURE'),
    text: _translate.instant('GeneralOriginal.LABELS.CANNOT_REVERT'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: _translate.instant('GeneralOriginal.AGREE'),
    cancelButtonText: _translate.instant('GeneralOriginal.CANCEL')
  }).then((result) => {
    if(result.isConfirmed) {
      localStorage.removeItem('deviceToHideArray');
      localStorage.removeItem('connectedDevices');
      swal.fire(
        _translate.instant('GeneralOriginal.SWAL.MESSAGES.LIST_CLEANED_SUCCESSFULLY'),
      );
      load_device;
      list_hide.length = 0;
    }
  })
}

/**
 * Allow open dialog for update the App
 * @param {TranslateService} _translate  - Translation service
 * @param {string} link - Url from App in playStore
 * @returns {Promise<void>}
 */
export const swalUpdateApp = (_translate: TranslateService, link: string): Promise<void> => {
  return swal.fire({
    title: _translate.instant('GeneralOriginal.UPDATE_REQUIRED'),
    text: _translate.instant('GeneralOriginal.UPDATE_MESSAGE'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: _translate.instant('GeneralOriginal.DOWNLOAD_NEW_APP_VERSION'),
    cancelButtonText: _translate.instant('GeneralOriginal.CLOSE_APP')
  }).then((result) => {
    if (result.isConfirmed) {
      window.location.href = link;
    } else {
      App.exitApp();
    }
  });
}

// Función para mostrar la ventana de carga
export function showLoading(): Promise<SweetAlertResult<any>> {
  return swal.fire({
    title: 'Por favor, espere...',
    text: 'Estamos procesando su solicitud.',
    allowOutsideClick: false, // Evita que el usuario cierre el diálogo haciendo clic fuera de él
    didOpen: () => {
      swal.showLoading(); // Muestra el spinner de carga
    }
  });
}

// Función para ocultar la ventana de carga
export function hideLoading(): void {
  swal.close(); // Cierra el diálogo
}


// Ajuste de swalWaitinganswerProtocol
export const swalWaitinganswerProtocol = (attempts: number, sendCommand: () => Promise<{ success: boolean; data: KeyboardVerification; }>): Promise<{ success: boolean; data: KeyboardVerification; }> => {
  let timerInterval: any;
  let finalResponse: { success: boolean; data: KeyboardVerification } = { success: false, data: null };
  const executeCommand = async (attemptsLeft: number): Promise<{ success: boolean; data: KeyboardVerification; }> => {
    if (attemptsLeft <= 0) {
      return finalResponse; // No more attempts left, return the final response
    }

    const response = await sendCommand();
    finalResponse = response;
    console.warn(finalResponse)

    if (response.success || attemptsLeft <= 1) {
      return response; // If successful or last attempt, resolve the final response
    } else {
      return executeCommand(attemptsLeft - 1); // Retry with remaining attempts
    }
  };
  return new Promise((resolve) => {
    const swalInstance = swal.fire({
      title: 'Esperando respuesta del protocolo',
      html: 'Esperando...',
      timer: 4000,
      timerProgressBar: true,
      didOpen: () => {
        swal.showLoading();
        const timer = swal.getPopup().querySelector('b');
        timerInterval = setInterval(() => {
          if (timer) {
            timer.textContent = `${Math.ceil(swal.getTimerLeft() / 1000)}s`;
          }
        }, 100);
      },
      willClose: () => {
        clearInterval(timerInterval);
      }
    }).then(() => {
      swal.close(); 

      if (finalResponse.success) {
        swal.fire({
          icon: 'success',
          title: 'Tiene protocolo',
          text: 'El protocolo está disponible',
          timer: 1500
        }).then(() => {
          resolve({ success: true, data: finalResponse.data });
        });
      } else {
        swal.fire({
          icon: 'error',
          title: 'No cuenta con protocolo',
          text: 'El protocolo no está disponible.',
          timer: 1500
        }).then(() => {
          resolve({ success: false, data: finalResponse.data });
        });
      }
    });
  });
};

export const swalKeyboardMatch = (match: boolean): Promise<boolean> => {
  return new Promise<boolean>((resolve) => {
    if (match) {
      swal.fire({
        icon: 'success',
        title: 'El teclado coincide!',
        text: 'LA referencia del teclado, al que quiere enviar el proyecto, corresponde a la referencia del proyecto.',
        timer: 4000
      }).then(() => {
        resolve(true)
      })
    }else {
      swal.fire({
        icon: 'error',
        title: 'El teclado No coincide!',
        text: 'LA referencia del teclado, al que quiere enviar el proyecto, No corresponde a la referencia del proyecto.',
        timer: 4000
      }).then(() => {
        resolve(false)
      })
    }
  })
}

// Función para mostrar la ventana de selección de idioma
export const swalSelectLanguage = (_translate: TranslateService): Promise<SweetAlertResult<any>> => {
  const languages: Record<number, string> = {
    0: _translate.instant('GeneralOriginal.SPANISH'),
    1: _translate.instant('GeneralOriginal.ENGLISH'),
    2: _translate.instant('GeneralOriginal.PORTUGUESE'),
  };
  return swal.fire({
    title: _translate.instant('Transfer.MESSAGES.LANGUAGE'),
    text: _translate.instant('Transfer.MESSAGES.SELECT_LANGUAGE'),
    input: 'select',
    inputOptions: languages,
    inputPlaceholder: _translate.instant('Device_admin.LABELS.SELECT_OPTION'),
    allowEscapeKey: false,
    allowEnterKey: false,
    showCancelButton: true,
    cancelButtonText: _translate.instant('GeneralOriginal.CANCEL'),
    confirmButtonText: _translate.instant('GeneralOriginal.SAVE'),
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-success",
      cancelButton: "btn",
      input: 'swal2-select form-control',
    },
    buttonsStyling: false
  });
}

export const swalSelectWarning15 = (_translate: TranslateService): Promise<SweetAlertResult<any>> => {
  return swal.fire({
    title: _translate.instant('Transfer.MESSAGES.WARNING_+15'),
    html: `
      <div style="display: flex; gap: 16px; justify-content: center; margin-top: 8px;">
        <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
          <input type="radio" name="swal-mode" value="0" checked />
          ${_translate.instant('Transfer.MESSAGES.UNLOCK')}
        </label>
        <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
          <input type="radio" name="swal-mode" value="1" />
          ${_translate.instant('Transfer.MESSAGES.ENABLE')}
        </label>
      </div>
    `,
    allowEscapeKey: false,
    allowEnterKey: false,
    showCancelButton: true,
    cancelButtonText: _translate.instant('GeneralOriginal.CANCEL'),
    confirmButtonText: _translate.instant('GeneralOriginal.SAVE'),
    reverseButtons: true,
    customClass: {
      confirmButton: 'btn btn-success',
      cancelButton: 'btn',
    },
    buttonsStyling: false,
    preConfirm: (): number => {
      const selected = (document.querySelector('input[name="swal-mode"]:checked') as HTMLInputElement)?.value;
      return Number(selected);
    }
  });
};

/**
 * Función para mostrar la ventana de cambio de nombre
 * @param {TranslateService} _translate - Servicio de traducción
 * @param {string} oldName - Nombre actual del proyecto
 * @returns {Promise<{ confirmed: boolean, name?: string}>}
 */
export const swalChangeProjectName = async (_translate: TranslateService, oldName: string): Promise<{ confirmed: boolean, name?: string}> => {
  const { value, isConfirmed }: SweetAlertResult<string> = await swal.fire({
    title: _translate.instant('Project_admin.LABELS.CHANGE_PROJECT_NAME'),
    text: `“${oldName}”`,
    input: 'text',
    inputPlaceholder: _translate.instant('Project_admin.LABELS.ENTER_PROJECT_NAME'),
    showCancelButton: true,
    cancelButtonText: _translate.instant('GeneralOriginal.CANCEL'),
    confirmButtonText: _translate.instant('GeneralOriginal.SAVE'),
    customClass: {
      'cancelButton': 'btn',
      'confirmButton': "btn btn-success",
      'input': 'form-control',
    },
    buttonsStyling: false,
    reverseButtons: true,
    inputValidator: (value) => {
      if (!value) {
        return _translate.instant('Project_admin.ERRORS.INPUT_EMPTY');
      } else if (value.length > MAX_CHARACTERS) {
        return _translate.instant('Project_admin.ERRORS.NAME_TOO_LONG');
      }
    }
  });
  return { confirmed: isConfirmed, name: value };
}